/**
 * Who signed up and where each customer is in onboarding: labs, questions, reviewing, checkout,
 * paid. Counts cover every customer; the table is the list, optionally one step.
 */
import * as stylex from "@stylexjs/stylex";
import type { AdminFunnel, AdminFunnelUser, FunnelStage } from "@lattice/core";
import { FUNNEL_STAGES, ROUTES } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { api } from "./api.ts";
import { Person, Truncate } from "./bits.tsx";
import type { Column } from "./layout.tsx";
import { FilterChip, FilterRow, PageHead, Table } from "./layout.tsx";
import { useLoad } from "./load.ts";
import { href, rememberList, rememberSearch } from "./router.ts";
import { ErrorText, Status } from "./ui.tsx";
import { day, FUNNEL_STAGE, refusal } from "./words.ts";
function totalOf(counts: AdminFunnel["counts"]): number {
  return FUNNEL_STAGES.reduce((sum, stage) => sum + counts[stage], 0);
}
const COLUMNS: readonly Column<AdminFunnelUser>[] = [{
  label: "Name",
  cell: u => <Person name={u.anonymous ? "Anonymous visitor" : u.name} email={u.anonymous ? null : u.email} to={href.user(u.id)} />
}, {
  label: "Email",
  cell: u => u.anonymous ? <span className="x1jty25b">No email yet</span> : <Truncate value={u.email} />
}, {
  label: "Stage",
  nowrap: true,
  cell: u => <Status tone={u.stage === "paid" ? "ok" : "neutral"}>{FUNNEL_STAGE[u.stage]}</Status>
}, {
  label: "Signed up",
  numeric: true,
  phoneLabel: true,
  cell: u => day(u.createdAt)
}];
function Loaded({
  funnel,
  stage
}: {
  readonly funnel: AdminFunnel;
  readonly stage: FunnelStage | null;
}) {
  const total = totalOf(funnel.counts);
  const title = stage === null ? "Signups" : FUNNEL_STAGE[stage];
  const empty = stage === null ? "No one has signed up yet. They appear here after the first upload." : `No one is in ${FUNNEL_STAGE[stage].toLowerCase()}. Choose All to see every signup.`;
  return <>
      <PageHead title={title} meta={`${total} ${total === 1 ? "customer" : "customers"}`} />
      <div className="x78zum5 xdt5ytf xd3upel">
        <FilterRow>
          <FilterChip to={href.funnel(null)} label="All" count={total} current={stage === null} />
          {FUNNEL_STAGES.map(step => <FilterChip key={step} to={href.funnel(step)} label={FUNNEL_STAGE[step]} count={funnel.counts[step]} current={stage === step} />)}
        </FilterRow>
        <Table label={stage === null ? "Customers" : FUNNEL_STAGE[stage]} columns={COLUMNS} rows={funnel.users} rowKey={u => u.id} empty={empty} />
      </div>
    </>;
}
export function Funnel({
  stage
}: {
  readonly stage: FunnelStage | null;
}) {
  rememberList(href.funnel(stage));
  rememberSearch("");
  const key = stage === null ? "funnel" : `funnel-${stage}`;
  const [found] = useLoad(key, () => api<AdminFunnel>(ROUTES.adminFunnel, stage === null ? {} : {
    query: {
      stage
    }
  }));
  if (found.state === "loading") return <PageHead title="Signups" meta="Loading" />;
  if (found.state === "failed") return <ErrorText>{refusal(found.error)}</ErrorText>;
  return <Loaded funnel={found.data} stage={stage} />;
}